import axios from 'axios';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { fetchRouteEvents } from './api/routingApi';
import { suggestedEventWindow } from './eventWindow';
import { PlannerState, RouteEventsReport } from './types';

/** Longer than the assets debounce: this call costs a web search and two model turns. */
const DEBOUNCE_MS = 1200;

export const DEFAULT_EVENT_CORRIDOR_M = 1500;
export const DEFAULT_HORIZON_HOURS = 72;

export interface UseRouteEvents {
  enabled: boolean;
  setEnabled: (on: boolean) => void;
  corridorM: number;
  setCorridorM: (metres: number) => void;
  horizonHours: number;
  setHorizonHours: (hours: number) => void;
  windowStart: string;
  windowEnd: string;
  setWindowStart: (iso: string) => void;
  setWindowEnd: (iso: string) => void;
  report: RouteEventsReport | null;
  loading: boolean;
  error: string | null;
  /** The report answers a question about a route or window we have since left. */
  stale: boolean;
  capturedAt: string | null;
  refresh: () => void;
}

/**
 * Scheduled disruptions near the selected route.
 *
 * Re-runs when the departure time changes as well as the geometry, because
 * `depart_at` is what decides which events we are actually there for — moving
 * a run from morning to evening can empty the list or fill it.
 *
 * The last report is held rather than cleared, for the same reason as the asset
 * scan and more so: this one costs a web search and two model turns, which is
 * far too expensive to discard because a waypoint moved fifty metres.
 */
export function useRouteEvents(state: PlannerState): UseRouteEvents {
  const [enabled, setEnabled] = useState(false);
  const [corridorM, setCorridorM] = useState(DEFAULT_EVENT_CORRIDOR_M);
  const [horizonHours, setHorizonHours] = useState(DEFAULT_HORIZON_HOURS);
  const customizedWindow = useRef(false);
  const [windowStart, setWindowStartState] = useState(() => suggestedEventWindow(null, null).start);
  const [windowEnd, setWindowEndState] = useState(() => suggestedEventWindow(null, null).end);
  const [report, setReport] = useState<RouteEventsReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [fetchedKey, setFetchedKey] = useState<string | null>(null);
  const [capturedAt, setCapturedAt] = useState<string | null>(null);
  const fetched = useRef<{ key: string; nonce: number } | null>(null);

  const primary = state.routes[state.primaryIndex] || null;
  const departAt = state.options.departAt || null;
  const durationS = primary?.duration ?? null;

  const geometryKey = useMemo(() => {
    const coords = (primary?.geometry?.coordinates || []) as number[][];
    if (coords.length < 2) return '';
    const first = coords[0];
    const last = coords[coords.length - 1];
    return `${coords.length}:${first[0].toFixed(4)},${first[1].toFixed(4)}`
      + `:${last[0].toFixed(4)},${last[1].toFixed(4)}`;
  }, [primary]);

  const requestKey = `${geometryKey}|${departAt}|${durationS}|${corridorM}|${horizonHours}|${windowStart}|${windowEnd}`;

  const suggested = useMemo(
    () => suggestedEventWindow(departAt, durationS, horizonHours),
    [departAt, durationS, horizonHours],
  );

  useEffect(() => {
    customizedWindow.current = false;
  }, [geometryKey]);

  useEffect(() => {
    if (customizedWindow.current) return;
    setWindowStartState(suggested.start);
    setWindowEndState(suggested.end);
  }, [suggested.start, suggested.end, geometryKey]);

  const setWindowStart = useCallback((iso: string) => {
    customizedWindow.current = true;
    setWindowStartState(iso);
  }, []);

  const setWindowEnd = useCallback((iso: string) => {
    customizedWindow.current = true;
    setWindowEndState(iso);
  }, []);

  const refresh = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return undefined;
    const coordinates = (primary?.geometry?.coordinates || []) as number[][];
    if (coordinates.length < 2) return undefined;
    if (fetched.current?.key === requestKey && fetched.current.nonce === nonce) {
      return undefined;
    }

    let cancelled = false;
    const controller = new AbortController();
    const timer = setTimeout(async () => {
      setLoading(true);
      setError(null);
      try {
        const next = await fetchRouteEvents(
          {
            coordinates,
            depart_at: departAt,
            duration_s: durationS,
            corridor_m: corridorM,
            horizon_hours: horizonHours,
            window_start: windowStart,
            window_end: windowEnd,
          },
          controller.signal,
        );
        if (cancelled) return;
        setReport(next);
        setFetchedKey(requestKey);
        setCapturedAt(new Date().toISOString());
        fetched.current = { key: requestKey, nonce };
      } catch (e: any) {
        if (cancelled || axios.isCancel(e) || e?.code === 'ERR_CANCELED') return;
        setError(e?.response?.data?.detail || 'Could not search for events along this route.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, requestKey, nonce]);

  return {
    enabled,
    setEnabled,
    corridorM,
    setCorridorM,
    horizonHours,
    setHorizonHours,
    windowStart,
    windowEnd,
    setWindowStart,
    setWindowEnd,
    report,
    loading,
    error,
    stale: !!report && fetchedKey !== requestKey,
    capturedAt,
    refresh,
  };
}
